import { Column } from './../../components/accounting/stripe/transaction.interface';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, of, filter, Subscription, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  contactsResultColumn,
  stripeSearchResultColumn,
  Filter,
  invoiceResultColumn,
  paymentFilters,
  statusFilters,
  importStatusFilters,
} from './search.model';

import { NavigationService } from '../../navigation.service';
import { NavigationEnd, Router } from '@angular/router';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './search.component.scss',
})
export class SearchComponent implements OnDestroy, OnInit {
  @Input() matchedNav: string = '';
  @Output() search = new EventEmitter<string>();
  @Output() selectedFilterValue = new EventEmitter<Filter>();
  @Output() onEnterKeyPressed = new EventEmitter<boolean>();
  @Input() searchFilteredData: any;
  @Input() suggestedFilters: Filter[] = [];
  @Input() panel: boolean = false;
  @Output() searchTermParam = new EventEmitter<string>();

  suggestedPaymentFilters: Filter[] = paymentFilters;
  suggestedStatusFilters: Filter[] = statusFilters;
  suggestedImprtStatusFilters: Filter[] = importStatusFilters;
  nestedFilters: Filter[] = [];
  activeFilters: Filter[] = [];

  searchControl = new FormControl('');
  searchTerm: any;
  showSuggestedPanel: boolean = false;
  filteredFilters: Observable<Filter[]> = of([]);

  currentPageSubscription: Subscription | null = null;
  page: string = '';
  searchTermKey: string = '';
  debounceTimer: number = 0;
  private routerSubscription: Subscription | null = null;
  private searchInput$ = new Subject<string>();
  @ViewChild('input') input!: ElementRef;

  constructor(
    private navigationService: NavigationService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
  ) {}

  @ViewChild('searchInput') searchInput!: ElementRef;

  columns: { header: string; label: string }[] = [];
  private lastPageName: string = '';
  isFilterSelected = false;
  ngOnInit(): void {
    console.log('searchFilteredData', this.searchFilteredData);
    this.currentPageSubscription =
      this.navigationService.currentPage$.subscribe((pageName) => {
        // Check if the page is changing from 'accounts' to 'invoice' or vice versa
        const pagesToClearSearch = ['Accounts', 'Payments', 'Invoice'];
        if (
          pagesToClearSearch.includes(this.lastPageName) &&
          pagesToClearSearch.includes(pageName)
        ) {
          this.searchTerm = '';
        }
        this.page = pageName;
        this.showSuggestedPanel = this.panel;

        // Now you can use this.page in your switch case
        this.handleColumns();
      });

    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        // Clear the search term on every route change
        this.searchTerm = '';
        this.showSuggestedPanel = this.panel;
      });

    this.searchInput$.pipe(debounceTime(300)).subscribe(() => {
      this.onSearchChange();
    });
  }

  handleColumns() {
    switch (this.page) {
      case 'Payments':
        this.columns = stripeSearchResultColumn.map((col: Column) => ({
          header: col.key,
          label: col.label,
        }));
        break;
      case 'Invoice':
        this.columns = invoiceResultColumn.map((col: Column) => ({
          header: col.key,
          label: col.label,
        }));
        console.log(this.columns);
        break;
      case 'Account':
        this.columns = contactsResultColumn.map((col: Column) => ({
          header: col.key,
          label: col.label,
        }));
        break;
    }
  }

  ngOnDestroy() {
    clearTimeout(this.debounceTimer);
    this.currentPageSubscription?.unsubscribe();
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    const isClickInsideComponent = this.elementRef.nativeElement.contains(
      event.target,
    );
    // Conditionally check if the searchInput element exists before accessing it.
    const isClickInsideDropdown =
      this.searchInput && this.searchInput.nativeElement.contains(event.target);

    // If the click is outside the entire component (input and dropdown), close the panel and blur the input.
    if (!isClickInsideComponent) {
      this.showSuggestedPanel = false;
      this.isFilterSelected = false;
      this.input.nativeElement.blur();
    } else {
      this.showSuggestedPanel = true;
      this.input.nativeElement.focus();
    }
  }

  onInputFocus(): void {
    // Show the dropdown when the input gets focus
    if (this.searchTerm.length <= 2) {
      this.showSuggestedPanel = true;
      this.searchFilteredData = [];
      this.isFilterSelected = false;

      // Reset to default filters when input is empty
      if (!this.searchTerm) {
        this.nestedFilters = [];
      }
    }
  }

  onInputBlur(event: FocusEvent): void {
    // A small delay allows the 'onClick' event (if the user clicked inside the dropdown) to fire first.
    setTimeout(() => {
      // If the panel is still visible, it means the user clicked inside the dropdown, so we re-focus the input.
      if (this.showSuggestedPanel) {
        this.input.nativeElement.focus();
      } else {
        // Otherwise, allow the blur to complete, removing the cursor.
        this.input.nativeElement.blur();
      }
    }, 50);
  }

  onEnterKey(): void {
    // If a filter is selected or search term is > 1 char, emit the search event
    if (this.isFilterSelected || this.searchTerm.length >= 2) {
      this.onEnterKeyPressed.emit(true);
      this.showSuggestedPanel = false;
    }
  }

  onSearchChange() {
    // this.isFilterSelected = false;

    if (this.searchTerm.length < 2) {
      this.searchFilteredData = [];
      this.showSuggestedPanel = true;
      this.isFilterSelected = false;
      return;
    }

    if (!this.searchTerm.split(':')[1]) {
      if (this.searchTerm.split(':')[0] === 'PaymentType') {
        this.nestedFilters = this.suggestedPaymentFilters;
        this.cdr.detectChanges();
      } else if (this.searchTerm.split(':')[0] === 'Status') {
        this.nestedFilters = this.suggestedStatusFilters;
        this.cdr.detectChanges();
      } else if (this.searchTerm.split(':')[0] === 'ImportStatus') {
        this.nestedFilters = this.suggestedImprtStatusFilters;
        this.cdr.detectChanges();
      }
    }

    // Now, with 2 or more characters, we can show the connected results panel
    this.showSuggestedPanel = true;
    const parts = this.searchTerm.split(':');
    const searchFilterKey = parts.length > 1 ? parts[0] : '';
    const searchValue =
      parts.length > 1 ? parts.slice(1).join(':') : this.searchTerm;

    if (searchFilterKey) {
      this.selectedFilterValue.emit(this.searchTerm);
    } else {
      this.search.emit(this.searchTerm);
    }
  }

  onSelect(item: any) {
    this.searchTermKey = item.key;

    // Handle special filter keys
    if (item.key === 'PaymentType') {
      this.nestedFilters = this.suggestedPaymentFilters;
      this.cdr.detectChanges();
    } else if (item.key === 'Status') {
      this.nestedFilters = this.suggestedStatusFilters;
      this.cdr.detectChanges();
    } else if (item.key === 'ImportStatus') {
      this.nestedFilters = this.suggestedImprtStatusFilters;
      this.cdr.detectChanges();
    } else {
      // Normal filters: emit directly
      this.searchTerm = `${item.key}:`;
      this.showSuggestedPanel = false;
      return;
    }

    this.searchTerm = `${item.key}:`;
    this.showSuggestedPanel = true;
    this.isFilterSelected = true;
    this.focusInput();
    if (this.searchTerm.length < 2) {
      this.searchFilteredData = [];
      this.showSuggestedPanel = true;
      this.isFilterSelected = false;
      return;
    }
  }

  onSelectNested(item: Filter) {
    // For nested filters, emit selected
    this.searchTerm = `${this.searchTermKey}:${item.value}`;
    this.selectedFilterValue.emit(this.searchTerm);

    this.nestedFilters = [];
    this.showSuggestedPanel = false;
  }

  private focusInput() {
    setTimeout(() => this.input?.nativeElement?.focus(), 0);
  }

  onSelectRow(item: any) {
    this.searchTermParam.emit(this.searchTerm);
    this.selectedFilterValue.emit(item);
  }

  // New method to display the filter in the input field
  displayFilter(filter: Filter): string {
    return this.searchTerm;
    // filter ? `${filter.key} ? ':' + ${this.searchTerm}: ''}` : this.searchTerm;
  }

  onSearchTipsClick(): void {
    window.open('https://docs.stripe.com/dashboard/search');
    console.log('Search tips clicked!'); // Replace with your actual logic
  }

  selectFilter(event: any): void {
    const selectedFilter = event.option.value as Filter;
    this.searchTerm = `${selectedFilter.key}:${selectedFilter.value || ''}`;
    this.showSuggestedPanel = true;
  }

  // This method is called from the template when a suggested filter is clicked
  onSelectFilter(filter: Filter): void {
    this.searchTerm = `${filter.key}:${filter.value || ''}`;
    this.showSuggestedPanel = true; // Keep panel open to show connected results
    // this.isFilterSelected = true;
    // Immediately emit the selected filter to trigger a search
    this.selectedFilterValue.emit(this.searchTerm);
  }
}
